import React, { useEffect, useState } from 'react'
import { BrowserRouter as Router, Switch, Route } from 'react-router-dom'
import { MuiThemeProvider, StylesProvider, Theme, createMuiTheme } from '@material-ui/core/styles'
import CssBaseline from '@material-ui/core/CssBaseline'

import { setAuthHeader } from 'utils'
import { useApiCall, useLogOut, useLogIn } from 'hooks'
import { Admin, TokenLoginResponse } from 'types'
import { tokenLogin } from 'api'
import { AdminContext, StyleContext } from 'contexts'
import { SnackbarContainer, snackbar, ProtectedRoutes } from 'components'
import { Colors, Style } from 'types/style'
import { useStyleContext } from 'contexts/StyleContext/useStyleContext'

import { Login } from './Login'
import { Main } from './Main'

const App: React.FC = () => {
  const [isAppLoading, setIsAppLoading] = useState(true)
  const [isThemeLoading, setIsThemeLoading] = useState(true)

  const styleContextValue = useStyleContext()

  const [admin, setAdmin] = useState<Admin | undefined>(() => {
    const localAdmin = localStorage.getItem('admin')
    if (!localAdmin) return undefined
    return JSON.parse(localAdmin)
  })

  const [tokenLoginApi] = useApiCall<void, TokenLoginResponse>(tokenLogin)

  useEffect(() => {
    document.title = process.env.REACT_APP_NAME!
  }, [])

  const [logIn] = useLogIn()
  const [logOut] = useLogOut()

  useEffect(() => {
    const token = localStorage.getItem('token')
    if (!token) {
      setIsAppLoading(false)
      return
    }

    const updateAdmin = async () => {
      try {
        setAuthHeader(token)
        const { user: updatedAdmin } = await tokenLoginApi()
        logIn(token, updatedAdmin)
      } catch (_) {
        logOut()
      } finally {
        setIsAppLoading(false)
      }
    }
    updateAdmin()
  }, [logIn, logOut, tokenLoginApi])

  const [theme, setTheme] = useState<Theme>(createMuiTheme())

  const { style, fetchAndSetStyle, isLoadingStyle } = useStyleContext()

  useEffect(() => {
    const applyTheme = (style: Style) => {
      const { storeColors } = style
      const newTheme = createMuiTheme({
        palette: {
          primary: {
            main: storeColors.PRIMARY_MAIN,
            contrastText: storeColors.PRIMARY_CONTRAST,
          },
          secondary: {
            main: storeColors.SECONDARY_MAIN,
          },
          background: {
            default: '#fff',
          },
        },
      })
      setTheme(newTheme)
      setIsThemeLoading(false)
    }

    const updateTheme = async () => {
      if (style) {
        applyTheme(style)
      } else {
        await fetchAndSetStyle()
      }
    }

    updateTheme()
  }, [isLoadingStyle])

  return (
    <>
      {!isThemeLoading ? (
        <StylesProvider injectFirst>
          <StyleContext.Provider value={styleContextValue}>
            <MuiThemeProvider theme={theme}>
              <CssBaseline />
              <AdminContext.Provider value={{ admin, setAdmin }}>
                <Router>
                  <Switch>
                    <Route path="/login" component={Login} />
                    <ProtectedRoutes>
                      <Main />
                    </ProtectedRoutes>
                  </Switch>
                </Router>
                <SnackbarContainer
                  ref={ref => {
                    snackbar.containerInstance = ref
                  }}
                />
              </AdminContext.Provider>
            </MuiThemeProvider>
          </StyleContext.Provider>
        </StylesProvider>
      ) : null}
    </>
  )
}

export { App }
